import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  companionSlots,
  pickerCandidates,
  seatRequestForCandidate,
} from '../../../lib/seatingCandidates';
import type { AssignSeatRequest, SeatingCandidate, SeatingTable } from '../../../types/seating';

/** A roster runs to 137 names; more than this on screen is a wall, not a list. */
const MAX_ROWS = 25;

const RSVP_LABELS: Record<string, string> = {
  going: 'Going',
  maybe: 'Maybe',
  declined: 'Declined',
  unknown: 'No reply',
};

/**
 * Who sits in this chair.
 *
 * Only guests who are not already seated appear. The database allows exactly one
 * chair per guest per chart, so offering a seated name is offering a button that
 * answers 409 — and a picker that lists everyone is how the same person ends up
 * at two tables on a printed chart.
 *
 * Nobody is filtered out for not having replied. On a real trip most of the
 * roster never answers, so RSVP ranks the list — going, then maybe, then no
 * reply — and never shortens it.
 */
export default function SeatPickerDialog({
  seatName,
  candidates,
  tables,
  saving,
  onClose,
  onSeat,
}: {
  seatName: string;
  candidates: SeatingCandidate[];
  tables: SeatingTable[];
  saving: boolean;
  onClose: () => void;
  /**
   * Failures are not shown here. Every recoverable refusal — occupied chair, no
   * claim on the dinner — is answered by a button on the page's alert, and the
   * editor closes this dialog so that button is reachable.
   */
  onSeat: (request: AssignSeatRequest, occupantName: string) => void;
}) {
  /*
    Starts on the people who said yes. Ranking alone put the 40 who are coming
    above the 97 who never replied and left the host to work out where one group
    ended — on a 137-name roster that is a wall, which is the exact thing the
    row cap was already trying to avoid.
  */
  const [query, setQuery] = useState('');
  const [goingOnly, setGoingOnly] = useState(true);

  const matches = useMemo(
    () => pickerCandidates(candidates, tables, query, goingOnly),
    [candidates, tables, query, goingOnly],
  );
  /* What the filter is holding back, so the number is on screen rather than implied. */
  const allMatches = useMemo(
    () => pickerCandidates(candidates, tables, query, false),
    [candidates, tables, query],
  );
  const hidden = allMatches.length - matches.length;
  const companions = useMemo(() => companionSlots(candidates, tables, query), [candidates, tables, query]);
  const shown = matches.slice(0, MAX_ROWS);

  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pb: 0.5 }}>Who sits here?</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">{seatName}</Typography>

          <TextField
            size="small"
            fullWidth
            autoFocus
            label="Find a guest"
            placeholder="Name, email or phone"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />

          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Chip
              size="small"
              color={goingOnly ? 'primary' : 'default'}
              variant={goingOnly ? 'filled' : 'outlined'}
              label={goingOnly ? 'Only guests who said yes' : 'Everyone on the list'}
              onClick={() => setGoingOnly((on) => !on)}
            />
            {goingOnly && hidden > 0 ? (
              <Typography variant="caption" color="text.secondary">
                {`${hidden} more ${hidden === 1 ? 'name' : 'names'} hidden — tap to show everyone`}
              </Typography>
            ) : null}
          </Stack>

          {shown.length > 0 ? (
            <Stack spacing={0.5}>
              {shown.map((candidate) => (
                <Box
                  key={candidate.inviteeId || candidate.displayName}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>{candidate.displayName}</Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary">
                        {RSVP_LABELS[candidate.rsvpStatus ?? ''] || 'No reply'}
                      </Typography>
                      {candidate.seatsNeeded > 1 ? (
                        <Chip size="small" variant="outlined" label={`party of ${candidate.seatsNeeded}`} />
                      ) : null}
                      {candidate.claimStatus === 'release_pending' ? (
                        <Chip size="small" color="warning" variant="outlined" label="giving up their spot" />
                      ) : null}
                    </Stack>
                  </Box>
                  <Button
                    size="small"
                    disabled={saving}
                    onClick={() => onSeat(seatRequestForCandidate(candidate), candidate.displayName)}
                  >
                    Seat
                  </Button>
                </Box>
              ))}
              {matches.length > shown.length ? (
                <Typography variant="caption" color="text.secondary">
                  Showing {shown.length} of {matches.length} — keep typing to narrow it down.
                </Typography>
              ) : null}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {query.trim()
                ? `Nobody unseated matches “${query.trim()}”.`
                : 'Everyone attending already has a chair.'}
            </Typography>
          )}

          {companions.length > 0 ? (
            <>
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Plus-one chairs still owed — a dinner claim for three needs three chairs.
                </Typography>
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  {companions.map((slot) => (
                    <Box
                      key={slot.inviteeId}
                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                    >
                      <Typography variant="body2" noWrap>
                        {slot.guestLabel}
                        {slot.remaining > 1 ? ` (${slot.remaining} to place)` : ''}
                      </Typography>
                      <Button
                        size="small"
                        disabled={saving}
                        onClick={() =>
                          onSeat(
                            {
                              seatKind: 'guest',
                              accompaniesInviteeId: slot.inviteeId,
                              guestLabel: slot.guestLabel,
                            },
                            slot.guestLabel,
                          )
                        }
                      >
                        Seat
                      </Button>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </>
          ) : null}

          <Divider />
          <Box>
            <Button
              size="small"
              disabled={saving}
              onClick={() => onSeat({ seatKind: 'blocked' }, 'nobody')}
            >
              Keep this chair empty
            </Button>
            <Typography variant="caption" color="text.secondary" display="block">
              A broken chair, a high chair, or the space next to the host.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
